import { useEffect, useMemo, useRef, useState } from 'react';

import { AttemptHistory } from './AttemptHistory';
import { QuizTimer } from './QuizTimer';
import { useAttempt } from './useAttempt';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { AttemptResult, QuizInfo } from '@/lib/api/assessments';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

export interface QuizRunnerProps {
  assessmentID: string;
  info: QuizInfo;
  /** Preview-as-student blocks every write, so the runner must not start one. */
  previewActive?: boolean;
}

/**
 * QuizRunner drives the timed-attempt lifecycle end to end: start/resume, one
 * question per screen, debounced autosave, auto-submit on expiry, and the
 * graded review afterwards.
 */
export function QuizRunner({ assessmentID, info, previewActive = false }: QuizRunnerProps) {
  const a = useAttempt(assessmentID, !previewActive);
  const [current, setCurrent] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const questions = useMemo(() => a.paper?.questions ?? [], [a.paper]);
  const answeredCount = useMemo(
    () => questions.filter((q) => a.answers[q.question_id] !== undefined).length,
    [questions, a.answers],
  );

  // Keep the cursor inside the paper when a resumed attempt is shorter than the
  // index left over from a previous render.
  useEffect(() => {
    if (current > 0 && current >= questions.length) setCurrent(Math.max(0, questions.length - 1));
  }, [current, questions.length]);

  // The expiry handler must fire once even though QuizTimer is remounted across
  // renders; the ref keeps the callback stable and the attempt hook itself
  // guards against a double submit.
  const submitRef = useRef(a.submit);
  submitRef.current = a.submit;

  if (previewActive) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          This is a timed quiz. Attempts cannot be started while previewing as a student.
        </p>
      </div>
    );
  }

  if (a.result) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <AttemptResultView result={a.result} />
        <AttemptHistory attempts={a.history} maxAttempts={info.max_attempts} />
      </div>
    );
  }

  // A submit that lands past the grace window closes and grades the attempt
  // server-side but returns no AttemptResult — show the resolved history
  // instead of leaving the student on a frozen, scoreless paper.
  if (a.closedWithoutResult) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
          Time is up. This attempt was closed and graded from your last saved answers.
        </p>
        <AttemptHistory attempts={a.history} maxAttempts={info.max_attempts} />
      </div>
    );
  }

  if (a.loading) {
    return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Loading quiz…</div>;
  }

  // ---- pre-start screen ---------------------------------------------------
  if (!a.paper) {
    const used = Math.max(info.attempts_used, a.history.length);
    const left = info.max_attempts - used;
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <h1 className="mb-1 text-xl font-bold">Timed quiz</h1>
        <ul className="mb-6 mt-3 space-y-1 text-sm text-muted-foreground">
          {info.duration_mins ? <li>⏱ {info.duration_mins} minutes once you start</li> : null}
          <li>
            📝 {info.questions_per_attempt ?? info.question_count} question
            {(info.questions_per_attempt ?? info.question_count) === 1 ? '' : 's'}
          </li>
          <li>
            🔁 {left > 0 ? `${left} of ${info.max_attempts}` : `0 of ${info.max_attempts}`} attempt
            {info.max_attempts === 1 ? '' : 's'} remaining
          </li>
          {info.closes_at ? <li>📅 Closes {new Date(info.closes_at).toLocaleString()}</li> : null}
        </ul>
        {a.staleExpiredAttempt && (
          <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
            Your previous attempt&apos;s time ran out while this page was closed. Starting a new
            attempt will close it out and grade whatever you had saved.
          </p>
        )}
        {a.startError && (
          <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
            {a.startError}
          </p>
        )}
        <button
          type="button"
          onClick={() => void a.start()}
          disabled={left <= 0}
          className="min-h-[44px] w-full rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-60 sm:w-auto"
        >
          {left <= 0 ? 'No attempts remaining' : 'Start quiz'}
        </button>
        <AttemptHistory attempts={a.history} maxAttempts={info.max_attempts} />
      </div>
    );
  }

  // ---- the paper ----------------------------------------------------------
  const q = questions[current];
  const expiresAt = a.paper.expires_at ? new Date(a.paper.expires_at) : null;
  const unanswered = questions.length - answeredCount;

  return (
    <div className="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-8">
      {/* Sticky header: on a phone the timer must stay on screen while the
          student scrolls a long question, not scroll away with the page. */}
      <div className="sticky top-0 z-20 -mx-4 mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-border bg-background/95 px-4 py-2 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="text-sm font-semibold">
          Question {current + 1}
          <span className="text-muted-foreground"> / {questions.length}</span>
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            Attempt {a.paper.attempt_no} of {a.paper.max_attempts}
          </span>
        </div>
        {expiresAt && a.clientNowAtStart ? (
          <QuizTimer
            expiresAt={expiresAt}
            serverNow={new Date(a.paper.server_now)}
            clientNowAtStart={a.clientNowAtStart}
            onExpire={() => submitRef.current('expiry')}
          />
        ) : (
          <span className="text-xs text-muted-foreground">Untimed</span>
        )}
      </div>

      {/* Navigator. flex-wrap, never a horizontal scroller: at 390px a 30-item
          navigator wraps onto several rows instead of overflowing the viewport. */}
      <nav aria-label="Question navigator" className="mb-4 flex flex-wrap gap-1.5">
        {questions.map((item, i) => {
          const done = a.answers[item.question_id] !== undefined;
          return (
            <button
              key={item.question_id}
              type="button"
              onClick={() => setCurrent(i)}
              aria-current={i === current ? 'true' : undefined}
              aria-label={`Question ${i + 1}${done ? ', answered' : ', not answered'}`}
              className={`h-9 w-9 rounded-md border text-xs font-semibold tabular-nums ${
                i === current
                  ? 'border-primary bg-primary text-primary-foreground'
                  : done
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border bg-card text-muted-foreground'
              }`}
            >
              {i + 1}
            </button>
          );
        })}
      </nav>

      {a.timedOut && (
        <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
          Time is up. Your last saved answers have been graded.
        </p>
      )}

      {q && (
        <div className="rounded-xl border border-border bg-card p-4 sm:p-5">
          <p className="mb-4 text-sm font-semibold sm:text-base">{q.question_text}</p>
          <div className="space-y-2">
            {q.options.map((opt, i) => {
              const selected = a.answers[q.question_id] === i;
              return (
                // min-h-[44px] is the tap target floor: anything smaller is a
                // miss-tap on a phone, and this is a page with a running clock.
                <label
                  key={i}
                  className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                    selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <input
                    type="radio"
                    name={`q_${q.question_id}`}
                    checked={selected}
                    disabled={a.timedOut}
                    onChange={() => a.select(q.question_id, i)}
                    className="h-4 w-4 shrink-0 accent-primary"
                  />
                  <span>
                    {LETTERS[i] ?? i + 1}. {opt}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {q.points} pt{q.points === 1 ? '' : 's'}
            {a.saving ? ' · saving…' : ''}
          </p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setCurrent((c) => Math.max(0, c - 1))}
          disabled={current === 0}
          className="min-h-[44px] flex-1 rounded-lg border border-border px-4 text-sm font-semibold disabled:opacity-50 sm:flex-none"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}
          disabled={current >= questions.length - 1}
          className="min-h-[44px] flex-1 rounded-lg border border-border px-4 text-sm font-semibold disabled:opacity-50 sm:flex-none"
        >
          Next
        </button>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={a.submitting}
          className="min-h-[44px] w-full rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-60 sm:ml-auto sm:w-auto"
        >
          {a.submitting ? 'Submitting…' : 'Submit quiz'}
        </button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {answeredCount}/{questions.length} answered · answers save automatically
      </p>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit this attempt?</AlertDialogTitle>
            <AlertDialogDescription>
              {unanswered > 0
                ? `You still have ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}. `
                : ''}
              You cannot change your answers after submitting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep working</AlertDialogCancel>
            <AlertDialogAction onClick={() => void a.submit('manual')}>Submit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * AttemptResultView renders the graded paper. When `key_revealed` is false the
 * server has withheld the answer key (attempts remain and the window is open):
 * correct_index is -1, explanations are blank and awarded is zeroed. In that
 * mode we must NOT mark anything right or wrong — a withheld key is not a
 * wrong answer, and per-question colouring would leak the key back.
 */
export function AttemptResultView({ result }: { result: AttemptResult }) {
  const percent = result.max_score > 0 ? Math.round((result.score / result.max_score) * 100) : 0;
  const tone = percent >= 70 ? 'text-green-500' : percent >= 50 ? 'text-yellow-500' : 'text-red-500';

  return (
    <div>
      <div className="mb-6 rounded-xl border border-border bg-card p-5 sm:p-6">
        <h2 className="mb-1 text-xl font-bold">
          {percent >= 80 ? '🎉' : percent >= 50 ? '👍' : '📚'} Attempt {result.attempt_no} of{' '}
          {result.max_attempts} complete
        </h2>
        <div className="mt-4 flex flex-wrap gap-8">
          <div>
            <div className={`text-3xl font-bold ${tone}`}>
              {result.score}/{result.max_score}
            </div>
            <div className="text-xs text-muted-foreground">This attempt</div>
          </div>
          <div>
            <div className={`text-3xl font-bold ${tone}`}>{percent}%</div>
            <div className="text-xs text-muted-foreground">Percentage</div>
          </div>
          <div>
            <div className="text-3xl font-bold">{result.best_score}</div>
            <div className="text-xs text-muted-foreground">Best score (counts)</div>
          </div>
        </div>
      </div>

      {!result.key_revealed && (
        <p className="mb-4 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Correct answers and explanations are released once you have used all your attempts (or the
          quiz closes). Your answers below are shown as you submitted them.
        </p>
      )}

      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {result.key_revealed ? 'Review' : 'Your answers'}
      </h3>
      <div className="space-y-3">
        {result.review.map((item, idx) => (
          <div key={item.question_id} className="rounded-lg border border-border bg-card p-4">
            <p className="mb-3 text-sm font-semibold">
              Q{idx + 1}. {item.question_text}
            </p>
            <div className="space-y-1.5">
              {item.options.map((opt, i) => {
                const chosen = item.chosen_index === i;
                const correct = result.key_revealed && item.correct_index === i;
                const wrong = result.key_revealed && chosen && item.correct_index !== i;
                return (
                  <div
                    key={i}
                    className={`rounded-md border px-3 py-2 text-xs ${
                      correct
                        ? 'border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400'
                        : wrong
                          ? 'border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400'
                          : chosen
                            ? 'border-blue-500/50 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                            : 'border-border text-muted-foreground'
                    }`}
                  >
                    {LETTERS[i] ?? i + 1}. {opt}
                    {chosen ? ' — your answer' : ''}
                    {correct && !chosen ? ' — correct answer' : ''}
                  </div>
                );
              })}
            </div>
            {result.key_revealed && item.explanation && (
              <p className="mt-3 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                {item.explanation}
              </p>
            )}
            {result.key_revealed && (
              <p className="mt-2 text-xs text-muted-foreground">
                {item.awarded}/{item.points} pt{item.points === 1 ? '' : 's'}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
